import {
  model,
  Model,
  prop,
  _async,
  _await,
  idProp,
  registerRootStore,
  modelAction,
  modelFlow,
  Frozen,
  frozen,
  Ref,
  rootRef,
  detach,
  getSnapshot,
} from "mobx-keystone";
import { computed, observable } from "mobx";
import { createContext } from "react";
import { has, last, max, min } from "lodash";
import { Bob, BobRef } from "./Bob";
import { CurrencySnapshot, EnvironmentData } from "../helpers/types";
import { BobEvent, BobEventKind } from "./BobEvent";
import { initEvents } from "../helpers/initEvents";
import { sendToBobController } from "../../helpers/requests";
import {
  HoldInvoice,
  HoldInvoiceSettings,
  isUndecidedHoldInvoice,
} from "../helpers/holdInvoices";
import { BOB_PAGES } from "../helpers/constants";

// the decision modal re-opens while anything is undecided and the user has
// not touched it for this long (per tab, D5)
export const HOLD_MODAL_RENAG_MS = 10 * 60 * 1000;

@model("exrails/BobsWorkspace")
export class BobsWorkspace extends Model({
  _version: prop(0),
  id: idProp,
  useWebAnalytics: prop<boolean>(false),
  accountIsTwoFactorProtected: prop<boolean>(false),
  accountRequiresTwoFactorFormForActions: prop<boolean>(false),
  accountTwoFactorIntervalMinutes: prop<number>(0),
  accountUserWantsBrowserNotifications: prop<boolean | null>(null),
  accountNumberOfServerKnownBrowserSubscriptions: prop<number>(0),
  accountHasAgreedToStablecoinRisks: prop<boolean>(false),
  email: prop<string>(""),
  accountUUid: prop<string>(""),
  hasLoaded: prop<boolean>(false),
  bobs: prop<Bob[]>(() => []),
  events: prop<BobEvent[]>(() => []),
  eventsDrawerIsShown: prop<boolean>(false),
  selectedBobRef: prop<Ref<Bob> | undefined>(undefined),
  baseDomain: prop<string>(""),
  exchangeRates: prop<Frozen<CurrencySnapshot> | null>(null),
  userHasGoneIdle: prop<boolean>(false),
  pageLoadedAtInteger: prop<number>(() => Date.now()),
  maxInvoiceSatoshis: prop<number>(25000000),
  minOnchainPaymentSizeSatoshis: prop<number | null>(null),
  minStablecoinPaymentSizeBaseUnits: prop<number | null>(null),
  vapidPublicKey: prop<string>(""),
  weKeepThisPlatformFeePercentageForOnchainPayments: prop<number | null>(null),
  weKeepThisPlatformFeePercentageForStablePayments: prop<number | null>(null),
  bobStableSolanaFixedPlatformFeeSatoshis: prop<number | null>(null),
  // hold invoices (zz-support-hold-invoices-fable.txt 10)
  holdInvoiceSettings: prop<Frozen<HoldInvoiceSettings> | null>(null),
  accountSendEmailForHoldInvoice: prop<boolean>(true),
  accountHoldInvoicesBlockedAt: prop<number | null>(null),
  accountHoldInvoicesBlockedReason: prop<string | null>(null),
  accountHoldInvoicesRefusalReason: prop<string | null>(null),
  // account-wide, matching the caps creation enforces (D1)
  holdOpenCountAccount: prop<number>(0),
  holdAcceptedCountAccount: prop<number>(0),
  lastHoldModalInteractionAt: prop<number>(0),
}) {
  @modelAction
  setHoldAccountCounts = (openCount: number, acceptedCount: number) => {
    this.holdOpenCountAccount = openCount ?? 0;
    this.holdAcceptedCountAccount = acceptedCount ?? 0;
  };

  @modelAction
  setAccountSendEmailForHoldInvoice = (value: boolean) => {
    this.accountSendEmailForHoldInvoice = value;
  };

  @modelAction
  markHoldModalInteraction = () => {
    this.lastHoldModalInteractionAt = Date.now();
  };

  // every undecided hold payment in every loaded vault, oldest deadline first:
  // the badge and the banner count these, so a payment waiting in another
  // vault is still visible (10.6)
  @computed
  get undecidedHoldInvoicesAcrossBobs(): { bob: Bob; hold: HoldInvoice }[] {
    const found: { bob: Bob; hold: HoldInvoice }[] = [];
    for (const bob of this.bobs) {
      for (const hold of bob.holdInvoices.data) {
        if (isUndecidedHoldInvoice(hold)) found.push({ bob, hold });
      }
    }
    return found.sort(
      (a, b) =>
        (a.hold.hold_decision_deadline_at ?? 0) -
        (b.hold.hold_decision_deadline_at ?? 0),
    );
  }

  @computed
  get canCreateHoldInvoices() {
    return !this.accountHoldInvoicesRefusalReason;
  }

  // Receive -> Hold Invoices is shown when the account can create them, or
  // when it is blocked but still has open/accepted ones to decide (10.2)
  @computed
  get showHoldInvoicesMenu() {
    return (
      this.canCreateHoldInvoices ||
      this.holdOpenCountAccount + this.holdAcceptedCountAccount > 0
    );
  }

  // ?hold=<uuid> from a push notification or an email: select the vault that
  // owns it, open the Hold Invoices page and its decision modal (D4)
  @modelAction
  openHoldInvoiceDeepLink = (uuid: string) => {
    const bob = this.bobs.find((b) =>
      b.holdInvoices.data.some((hold) => hold.uuid === uuid),
    );
    if (!bob) return false;
    if (this.selectedBob !== bob) {
      bob.sendDataAndDirectiveToRails("select-bob", {
        uuidOfBobToSelect: bob.uuid,
      });
      this.selectBob(bob);
    }
    bob.setOpenPage(BOB_PAGES.HOLD_INVOICES);
    const hold = bob.holdInvoices.data.find((h) => h.uuid === uuid);
    if (hold && isUndecidedHoldInvoice(hold)) {
      bob.openHoldDecisionModal(uuid, true);
    }
    return true;
  };

  // runs every few seconds: re-opens the decision modal on the selected vault
  // while anything there is undecided and the user has left it alone for
  // 10 minutes
  @modelAction
  maybeRenagHoldDecision = () => {
    const bob = this.selectedBob;
    if (!bob) return;
    if (bob.undecidedHoldInvoices.length === 0) return;
    if (Date.now() - this.lastHoldModalInteractionAt < HOLD_MODAL_RENAG_MS)
      return;
    // already showing it (maybe for a payment the user picked): leave it be;
    // any other modal in use: the badge and banner keep nagging instead
    if (bob.openModal) return;
    bob.openHoldDecisionModal(bob.undecidedHoldInvoices[0].uuid);
  };

  @modelAction
  setHasAgreedToStablecoinRisks = (value: boolean) => {
    console.log("setting hasAgreedToStablecoinRisks to", value);
    this.accountHasAgreedToStablecoinRisks = value;
  };

  @modelFlow
  agreeToStablecoinRisks = _async(function* (this: BobsWorkspace) {
    console.log("agreeing to stablecoin risks");
    const message = {
      bobUuid: this.selectedBob?.uuid,
    };
    const response = yield* _await(
      sendToBobController(message, "/bob_life/agree_to_stablecoin_risks"),
    );
    if (response.success) {
      this.setHasAgreedToStablecoinRisks(true);
    }
    return response;
  });

  @modelAction
  setUserHasGoneIdle = (value: boolean) => {
    console.log("setting userHasGoneIdle to", value);
    this.userHasGoneIdle = value;
  };
  @modelAction
  makeEvent = (
    title: string,
    message: string,
    kind: BobEventKind,
    payRequest: string | null = null,
    uuid: string | null = null,
  ) => {
    const event = new BobEvent({
      title,
      message,
      kind,
      payRequest,
      uuid,
    });
    this.events.push(event);
    return event;
  };

  @modelAction
  setEventsDrawerIsShown = (isShown: boolean) => {
    this.eventsDrawerIsShown = isShown;
  };

  @computed
  get totalSafeBalanceOfAllBobs() {
    return this.bobs.reduce((total, bob) => {
      return total + bob.safeBalanceInSatoshis;
    }, 0);
  }

  @computed
  get selectedBob() {
    return this.selectedBobRef ? this.selectedBobRef.current : undefined;
  }
  @modelAction
  selectBob(bob: Bob | undefined) {
    if (bob && !this.bobs.includes(bob)) throw new Error("unknown bob");
    this.selectedBobRef = bob ? BobRef(bob) : undefined;
  }

  @modelAction
  getBobByUuid = (uuid: string) => {
    const bob = this.bobs.find((b) => b.uuid === uuid);
    if (!bob) throw new Error("Bob not found");
    return bob;
  };

  @modelAction
  hydrateDataFromPage = () => {
    console.log("hydrate bob data");
    const scriptEl = document.getElementById(
      "__bob_bootstrap",
    ) as HTMLScriptElement | null;
    if (!scriptEl || !scriptEl.textContent)
      throw new Error("missing bootstrap JSON");
    const json = JSON.parse(scriptEl.textContent);
    console.log("hydrating bob data from page");

    this.useWebAnalytics = json.account_data.use_web_analytics;
    this.accountIsTwoFactorProtected =
      json.account_data.accountIsTwoFactorProtected;
    this.email = json.account_data.email;
    this.accountUUid = json.account_data.account_uuid;
    this.baseDomain = json.environment.base_domain;
    this.pageLoadedAtInteger = json.environment.page_loaded_at_server_time;
    this.maxInvoiceSatoshis = json.environment.max_invoice_satoshis;
    this.minOnchainPaymentSizeSatoshis =
      json.environment.min_onchain_payment_size_satoshis;
    this.minStablecoinPaymentSizeBaseUnits =
      json.environment.min_stablecoin_payment_size_base_units;

    this.vapidPublicKey = json.environment.vapid_public_key;
    this.exchangeRates = frozen(json.environment.exchange_rates);
    this.accountTwoFactorIntervalMinutes =
      json.account_data.two_fa_interval_minutes;
    this.accountUserWantsBrowserNotifications =
      json.account_data.user_wants_browser_notifications;
    this.accountNumberOfServerKnownBrowserSubscriptions =
      json.account_data.number_of_server_known_browser_subscriptions;
    this.accountHasAgreedToStablecoinRisks =
      json.account_data.has_agreed_to_stablecoin_risks;
    this.weKeepThisPlatformFeePercentageForOnchainPayments = Number(
      json.environment
        .we_keep_this_platform_fee_percentage_for_onchain_payments,
    );
    this.weKeepThisPlatformFeePercentageForStablePayments = Number(
      json.environment.we_keep_this_platform_fee_percentage_for_stable_payments,
    );
    this.bobStableSolanaFixedPlatformFeeSatoshis = Number(
      json.environment.bob_stable_solana_fixed_platform_fee_satoshis,
    );
    this.holdInvoiceSettings = json.hold_invoice_settings
      ? frozen(json.hold_invoice_settings)
      : null;
    this.accountSendEmailForHoldInvoice =
      json.account_data.send_email_for_hold_invoice !== false;
    this.accountHoldInvoicesBlockedAt =
      json.account_data.hold_invoices_blocked_at ?? null;
    this.accountHoldInvoicesBlockedReason =
      json.account_data.hold_invoices_blocked_reason ?? null;
    this.accountHoldInvoicesRefusalReason =
      json.account_data.hold_invoices_refusal_reason ?? null;
    this.setHoldAccountCounts(
      json.account_data.open_count_account,
      json.account_data.accepted_count_account,
    );

    this.updateBobs(json.bobs);
    this.hasLoaded = true;
    initEvents(this);
    //console.log("fully loaded", getSnapshot(this));
    console.log("bob data hydrated");
    console.log(this.baseDomain);
  };

  @modelAction
  updateBobs = (bobDatas) => {
    // console.log("update bobs", bobDatas);
    for (const bobData of bobDatas) {
      let existingBob = this.bobs.find((b) => b.uuid === bobData.uuid);

      if (!existingBob) {
        existingBob = new Bob({});
        this.bobs.push(existingBob);
      }
      existingBob.update(bobData);
      if (bobData.is_selected_by_user) {
        this.selectBob(existingBob);
      }
    }
  };
}

export const runCreateBobsWorkspace = () => {
  const workspace = new BobsWorkspace({});

  registerRootStore(workspace);
  return workspace;
};

const createdWorkspace = runCreateBobsWorkspace();
//console.log("created workspace", getSnapshot(createdWorkspace));
export const BobsWorkspaceContext =
  createContext<BobsWorkspace>(createdWorkspace);

export interface BobsWorkspaceInterface {
  BobsWorkspace: BobsWorkspace;
}
